import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useOwnerEditableSubmissionQuery } from '@/features/submissions/api';
import { PublicSubmissionForm } from '@/features/submissions/components/public/PublicSubmissionForm';
import { Alert, Card, CardContent, Skeleton } from '@/shared/components/ui';
import { PageTransition } from '@/shared/motion';

const searchSchema = z.object({
  token: z.string().min(1).optional(),
});

export const Route = createFileRoute('/_public/edit-request')({
  validateSearch: (search) => searchSchema.parse(search),
  component: EditRequestRoute,
});

function EditRequestRoute() {
  const { token } = Route.useSearch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.startsWith('ar') ?? false;
  const query = useOwnerEditableSubmissionQuery(token);
  const [saved, setSaved] = useState(false);

  if (!token) {
    return (
      <PageTransition>
        <Alert tone="warning">
          {t('submit.edit.missingToken', 'Missing edit link. Open the link from your email.')}
        </Alert>
      </PageTransition>
    );
  }

  if (query.isPending) {
    return (
      <PageTransition>
        <Card>
          <CardContent className="flex flex-col gap-4 p-8">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </PageTransition>
    );
  }

  if (query.isError) {
    const message =
      (query.error as Error & { apiError?: { message?: string } }).apiError?.message ??
      t(
        'submit.edit.unavailable',
        'This request can no longer be edited. It may have been reviewed or the link has expired.',
      );
    return (
      <PageTransition>
        <Alert tone="warning">{message}</Alert>
      </PageTransition>
    );
  }

  if (saved) {
    return (
      <PageTransition>
        <Card>
          <CardContent
            dir={isRtl ? 'rtl' : 'ltr'}
            className="flex flex-col items-center gap-3 p-8 text-center"
          >
            <h2 className="text-xl font-semibold text-foreground">
              {t('submit.edit.savedTitle', 'Changes saved')}
            </h2>
            <p className="max-w-sm text-sm text-foreground-muted">
              {t(
                'submit.edit.savedDescription',
                'Your request has been updated and is still under review.',
              )}
            </p>
          </CardContent>
        </Card>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <PublicSubmissionForm
        mode="edit"
        editToken={token}
        initial={query.data}
        onSuccess={() => setSaved(true)}
      />
    </PageTransition>
  );
}
